import { Box, useTheme } from "@mui/material";
import { BarDatum } from "@nivo/bar";
import React, { useEffect, useState, useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { EChartsOption } from "echarts";
import LegendsCharts from "../../legends";
import handleColors from "../utils/handleColors";
import useStyles from "../styles";
import { MyBarDashConfig } from "./types";
import maskUnits from "../../../utils/maskUnits";
import { formatData } from "../../../functions/formatData";
import { accumulatedData } from "../../../functions/formatData/acumulator";
import { usePages } from "../../../context/pageContext";
import parseKeyToColomns from "../../../functions/formatData/parseKeytoColumn";
import { shoterNames } from "../../../functions/formatData/shorterNames";
import { getColorsArray } from "../../../utils/getColorsPallet";
import calculateMetrics from "../utils/calculateMetrics";

const BarChart: React.FC<MyBarDashConfig> = (props) => {
  const {
    colorsByKey,
    units,
    fractionDigits,
    groupMode,
    indexBy = "id", // Use 'id' as default x-axis key
    groupBy,
    interval,
    viewMode,
    groupMethod,
    parseKeyToColumn,
    metricsType,
    enableTotals,
    colors,
  } = props;
  const { data, keys, ...otherProps } = props;

  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);

  const barWrapperRef = useRef<HTMLDivElement>(null);
  const echartsRef = useRef<ReactECharts>(null);

  const theme = useTheme();
  const styles = useStyles(theme);
  const { filterDate } = usePages();

  useEffect(() => {
    const element = barWrapperRef.current;
    if (!element) return;

    const measure = () => {
      if (element) {
        const { width, height } = element.getBoundingClientRect();
        if (width > 0 && height > 0) {
          setBoxWidth(width);
          setBoxHeight(height);
        }
      }
    };
    measure();

    const timer = setTimeout(measure, 500);

    let resizeTimer: ReturnType<typeof setTimeout>;
    const resizeObserver = new ResizeObserver((entries) => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) return;
          const entry = entries[0];
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            setBoxWidth(width);
            setBoxHeight(height);
          }
        });
      }, 50);
    });
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
      clearTimeout(resizeTimer);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (boxWidth && boxHeight && echartsRef.current) {
      const instance = echartsRef.current.getEchartsInstance();
      instance?.resize({ width: boxWidth, height: boxHeight });
    }
  }, [boxWidth, boxHeight]);

  // --- Data Processing ---
  const { formattedData, keyNames, formattedDataAccumulated } = useMemo(() => {
    if (!keys)
      return {
        formattedData: [],
        keyNames: [],
        formattedDataAccumulated: [],
      };
    const { parsedData, newKeys } = parseKeyToColomns(
      [...data],
      keys,
      indexBy,
      parseKeyToColumn,
    );

    if (groupBy !== "date") {
      return {
        formattedData: shoterNames(parsedData) as BarDatum[],
        keyNames: newKeys,
        formattedDataAccumulated: [],
      };
    }

    const formatted = formatData({
      data: parsedData,
      indexBy,
      keys: newKeys,
      formatSeries: "default",
      groupBy,
      interval,
      groupMethod,
    });

    const accumulated =
      (viewMode === "accumulate" &&
        accumulatedData({
          data: formatted.formattedData,
          keys: newKeys,
        })) ||
      [];

    return {
      formattedData: formatted.formattedData,
      keyNames: formatted.keyNames,
      formattedDataAccumulated: accumulated,
    };
  }, [
    data,
    keys,
    indexBy,
    interval,
    viewMode,
    filterDate,
    groupBy,
    groupMethod,
    parseKeyToColumn,
  ]);

  const activeData =
    viewMode === "accumulate" ? formattedDataAccumulated : formattedData;

  // --- ECharts Configuration ---

  const chartOption: EChartsOption = useMemo(() => {
    let colorPalette: string[] = [];
    if (colors) {
      colorPalette = getColorsArray(
        keyNames.length,
        colors,
        theme.palette.mode === "dark",
      );
    } else if (colorsByKey) {
      colorPalette = keyNames.map((key) =>
        handleColors(key, theme, colorsByKey),
      );
    } else {
      colorPalette = getColorsArray(
        keyNames.length,
        "nivo",
        theme.palette.mode === "dark",
      );
    }

    const currentWidth = boxWidth || 0;
    const countSticks = Number(currentWidth || 1000) / 100;
    const intervalCalc = Math.ceil(activeData.length / countSticks) - 1;

    const yAxisFormatter = (value: number) => maskUnits(value, units, 0);

    // Build bar series
    const series: any = keyNames.map((key, idx) => ({
      name: key,
      type: "bar",
      large: true,
      stack: groupMode !== "grouped" ? "total" : undefined,
      itemStyle: {
        borderRadius:
          groupMode !== "grouped" && idx === keyNames.length - 1
            ? [4, 4, 0, 0]
            : [0, 0, 0, 0],
      },
      label: {
        show: false,
      },
      encode: {
        x: indexBy,
        y: key,
      },
      tooltip: {
        trigger: "item",
        confine: true,
        backgroundColor: theme.palette.background.default,
        textStyle: { color: theme.palette.text.primary },
        valueFormatter: (value: number) =>
          maskUnits(value, units, fractionDigits),
      },
    }));

    // Add invisible series for totals on stacked bars
    if (groupMode !== "grouped" && enableTotals) {
      series.push({
        name: "_total_invisible",
        type: "bar",
        stack: "total",
        silent: true,
        itemStyle: {
          color: "transparent",
        },
        label: {
          show: true,
          position: "top",
          color: theme.palette.text.primary,
          fontWeight: "bold",
          formatter: function (params: any) {
            let sum = 0;
            const dataItem = activeData[params.dataIndex];
            for (const k of keyNames) {
              const value = Number(dataItem?.[k]);
              if (!isNaN(value)) sum += value;
            }
            return sum > 0 ? maskUnits(sum, units, fractionDigits) : "";
          },
        },
        labelLayout: {
          hideOverlap: true,
        },
        encode: {
          x: indexBy,
          y: "_invisible_value",
        },
        emphasis: {
          focus: "none",
        },
        tooltip: {
          show: false,
        },
      });

      activeData.forEach((item: any) => {
        item._invisible_value = 0;
      });
    }
    const grid = {
      top: 20,
      right: 24,
      bottom: 10,
      left: 10,
      containLabel: true,
    };

    return {
      animation: true,
      color: colorPalette,
      grid: grid,
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        confine: true,
      },
      dataset: {
        source: activeData,
      },
      xAxis: {
        type: "category",
        axisTick: { show: true },
        axisLine: { show: false },
        axisLabel: {
          fontSize: 10,
          color: theme.palette.text.secondary,
          interval: Math.max(0, intervalCalc),
          hideOverlap: true,
        },
      },
      yAxis: {
        type: "value",
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            color: theme.palette.divider,
            opacity: 0.5,
          },
        },
        axisLabel: {
          formatter: yAxisFormatter,
          fontSize: 10,
          color: theme.palette.text.secondary,
        },
      },
      series: series as any,
    };
  }, [
    activeData,
    keyNames,
    colors,
    colorsByKey,
    theme,
    groupMode,
    boxWidth,
    units,
    fractionDigits,
    indexBy,
    enableTotals,
  ]);

  const finalOption = {
    ...chartOption,
  };

  return formattedData.length === 0 ? undefined : (
    <Box
      sx={{
        ...styles.boxContainer,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          ...styles.boxLegend,
          minHeight: metricsType ? "45px" : "25px",
          flexShrink: 0,
        }}
      >
        <LegendsCharts
          {...props}
          keys={[...keyNames]}
          metricsType={
            metricsType
              ? calculateMetrics(metricsType, data as any[])
              : undefined
          }
        />
      </Box>
      <Box
        sx={{
          ...styles.boxContainerSizeChart,
          overflow: "hidden",
          minWidth: 0,
          width: "100%",
          flexGrow: 1,
          height: "100%",
          position: "relative",
        }}
        ref={barWrapperRef}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <ReactECharts
            ref={echartsRef}
            option={finalOption}
            style={{ height: "100%", width: "100%" }}
            notMerge={true}
            lazyUpdate={true}
            autoResize={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default BarChart;
